import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { GRADES_SUMMARY_TAB, subjectAttendanceLink } from '../../../shared/constant/assessmentTabs';
import { PASSING_GRADE } from '../../../shared/constant/grading';
import { showAlert } from '../../../shared/utils/dialogs';
import { ABSENT_RATE_THRESHOLD, NEAR_FAILING_MARGIN } from '../services/attentionService';
import StudentAttendanceModal from '../../AttendanceSummary/components/StudentAttendanceModal';

const studentName = (student) => `${student.lastname}, ${student.firstname}`;

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// Each grade opens the subject's Grades Summary on the term it was flagged in, filtered to the student's section.
const gradeLink = (student, g) => {
  const params = new URLSearchParams({ tab: GRADES_SUMMARY_TAB, term: String(g.term) });
  if (student.section) params.set('section', student.section);
  return `/dashboard/grades/${g.subjectId}?${params}`;
};

// One card per flagged subject, since each opens that subject's page. `reason` makes the cards
// markable as addressed; near-failing ones aren't.
const gradeCards = (reason) => (entries) =>
  entries.flatMap((entry) =>
    entry.grades.map((g) => ({
      key: `${entry.student.id}|${g.subjectId}`,
      student: entry.student,
      message: `${g.subject}: ${g.grade} (Term ${g.term})`,
      to: gradeLink(entry.student, g),
      review: g.review,
      flag: reason && { reason, studentId: entry.student.id, subjectId: g.subjectId, term: g.term, grade: g.grade },
    }))
  );

// Absences are totalled across the teacher's subjects, so they open the student's attendance summary
// (every subject, all dates) in a modal instead of a page; `attendanceIndex` is their place in the list.
const absenceCards = (entries) =>
  entries.map((entry, index) => ({
    key: entry.student.id,
    student: entry.student,
    message: `${plural(entry.absent, 'absence')} in ${plural(entry.counted, 'class day')} (${Math.round(entry.rate * 100)}%)`,
    attendanceIndex: index,
    review: entry.review,
    flag: { reason: 'absenteeism', studentId: entry.student.id, absent: entry.absent },
  }));

// The ranges in the titles come from the same constants the list is built with.
const GROUPS = [
  {
    key: 'failing',
    title: `Failing Grade (Below ${PASSING_GRADE})`,
    guidance: 'Recommend remedial classes and extra activities, and talk with the student about their progress.',
    badge: 'bg-red-50 text-red-600',
    cards: gradeCards('failing'),
  },
  {
    key: 'nearlyFailing',
    title: `At Risk of Failing (${PASSING_GRADE}–${PASSING_GRADE + NEAR_FAILING_MARGIN - 1})`,
    guidance: 'Give extra practice and monitor the student closely in the next assessments.',
    badge: 'bg-amber-50 text-amber-600',
    cards: gradeCards(null),
  },
  {
    key: 'absenteeism',
    title: `Frequent Absences (${Math.round(ABSENT_RATE_THRESHOLD * 100)}% or More)`,
    guidance: 'Talk with the student and contact their parent or guardian about the absences.',
    badge: 'bg-amber-50 text-amber-600',
    cards: absenceCards,
  },
];

// Addressed cards go last, keeping the list's order otherwise.
const addressedLast = (cards) => [...cards.filter((c) => !c.review), ...cards.filter((c) => c.review)];

// The students in a teacher's classes who need attention and guidance, grouped by why. Failing and
// absence cards can be marked as addressed,
// which takes them off the notification bell until things get worse (attentionService). Addressed
// cards are hidden here too unless "Show addressed" is on.
function AttentionSection({ attention, subjects, loading, error, markAddressed, undoAddressed }) {
  const groups = GROUPS;
  const [showAddressed, setShowAddressed] = useState(false);
  const allCardsByGroup = attention
    ? Object.fromEntries(groups.map((g) => [g.key, addressedLast(g.cards(attention[g.key]))]))
    : {};
  const cardsByGroup = Object.fromEntries(
    Object.entries(allCardsByGroup).map(([key, cards]) => [key, showAddressed ? cards : cards.filter((c) => !c.review)])
  );
  const allCards = Object.values(allCardsByGroup).flat();
  const openCount = allCards.filter((c) => c.flag && !c.review).length;
  const addressedCount = allCards.filter((c) => c.review).length;
  // Index into the absenteeism list of the student whose attendance modal is open.
  const [attendanceIndex, setAttendanceIndex] = useState(null);
  // The card whose mark is being saved, as `${group}|${card}`.
  const [savingKey, setSavingKey] = useState(null);
  const absentStudents = attention ? attention.absenteeism.map((entry) => entry.student) : [];
  const gridClass = 'grid grid-cols-1 lg:grid-cols-3 gap-3';

  const handleMark = async (groupKey, card) => {
    setSavingKey(`${groupKey}|${card.key}`);
    try {
      if (card.review) await undoAddressed(card.review);
      else await markAddressed(card.flag);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to save' });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="space-y-3" aria-label="Students needing attention and guidance">
      <div className="flex items-center gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
        <h2 className="text-lg font-semibold text-[#0b4a71]">Students Needing Attention and Guidance</h2>
        {openCount > 0 && (
          <span className="rounded-full bg-red-500 px-2 text-xs font-semibold leading-5 text-white">{openCount}</span>
        )}
        {addressedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAddressed((value) => !value)}
            aria-pressed={showAddressed}
            className="ml-auto flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-white"
          >
            {showAddressed ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            {showAddressed ? 'Hide addressed' : `Show addressed (${addressedCount})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className={gridClass}>
          {groups.map((g) => (
            <div key={g.key} className="bg-white rounded-lg p-4 space-y-2">
              <div className="h-5 w-1/2 rounded bg-gray-100 animate-pulse" />
              <div className="h-4 w-full rounded bg-gray-100 animate-pulse" />
              <div className="h-4 w-4/5 rounded bg-gray-100 animate-pulse" />
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
      ) : Object.values(cardsByGroup).every((cards) => cards.length === 0) ? (
        <p className="flex items-center gap-2 bg-white rounded-lg p-4 text-sm text-gray-500">
          <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
          {addressedCount > 0
            ? 'Every flagged student has been addressed.'
            : 'No students need attention or guidance right now.'}
        </p>
      ) : (
        <div className={gridClass}>
          {groups.map((group) => {
            const cards = cardsByGroup[group.key];
            return (
              <div key={group.key} className="min-w-0 flex flex-col bg-white rounded-lg p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${group.badge}`}>{group.title}</span>
                  <span className="text-xs text-gray-500">{cards.length}</span>
                </div>
                <p className="mt-2 text-xs text-gray-500">{group.guidance}</p>

                {cards.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-400">None</p>
                ) : (
                  <ul className="mt-2 max-h-80 overflow-y-auto space-y-2 pr-1">
                    {cards.map((card) => {
                      const opensSomething = Boolean(card.to) || card.attendanceIndex !== undefined;
                      const content = (
                        <>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#0b4a71] break-words">{studentName(card.student)}</p>
                            <p className="text-xs text-gray-500">
                              {card.student.section}
                              <span
                                className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                                  card.student.section ? 'ml-1' : ''
                                } ${group.badge}`}
                              >
                                {card.message}
                              </span>
                            </p>
                          </div>
                          {opensSomething && <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
                        </>
                      );
                      const mainClass = 'flex w-full items-center gap-3 px-3 py-2 text-left';
                      const saving = savingKey === `${group.key}|${card.key}`;
                      return (
                        <li
                          key={card.key}
                          className={`rounded-md border ${card.review ? 'border-green-200 bg-green-50/50' : 'border-gray-200'} ${
                            opensSomething ? 'hover:border-[#4ea6d0]' : ''
                          }`}
                        >
                          {card.to ? (
                            <Link to={card.to} className={mainClass}>
                              {content}
                            </Link>
                          ) : card.attendanceIndex !== undefined ? (
                            <button type="button" onClick={() => setAttendanceIndex(card.attendanceIndex)} className={mainClass}>
                              {content}
                            </button>
                          ) : (
                            <div className={mainClass}>{content}</div>
                          )}

                          {card.flag && (
                            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-3 py-1.5">
                              {card.review && (
                                <span className="mr-auto flex items-center gap-1 text-xs font-medium text-green-700">
                                  <CheckCircleIcon className="h-4 w-4" />
                                  Addressed
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleMark(group.key, card)}
                                disabled={saving}
                                className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50 ${
                                  card.review
                                    ? 'text-gray-500 hover:bg-gray-100'
                                    : 'border border-[#4ea6d0] text-[#0b4a71] hover:bg-[#4ea6d0]/10'
                                }`}
                              >
                                {card.review ? (
                                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                                ) : (
                                  <CheckIcon className="h-3.5 w-3.5" />
                                )}
                                {saving ? 'Saving…' : card.review ? 'Undo' : 'Mark as Addressed'}
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {attendanceIndex !== null && absentStudents[attendanceIndex] && (
        <StudentAttendanceModal
          students={absentStudents}
          index={attendanceIndex}
          subjects={subjects}
          subjectLink={(subjectId, student) => subjectAttendanceLink(subjectId, student.section)}
          onIndexChange={setAttendanceIndex}
          onClose={() => setAttendanceIndex(null)}
        />
      )}
    </section>
  );
}

export default AttentionSection;
